/**
 * NACH (National Automated Clearing House) type definitions
 */

export interface NachRepayment {
  FailureReason: number;
  LoanId: string;
  Amount: number;
  StartedOn: string;
  CompletedOn: string;
  IsSuccess: boolean | null;
}

export interface NachStatus {
  LoanId: string;
  Status: NachRegistrationStatus;
  BankName: string;
  AccountNumber: string;
  MaxAmount: number;
  RegistrationDate: string;
  ExpiryDate: string;
}

export interface EnachStatus {
  LoanId: string;
  Status: string;
  BankName: string;
  AccountNumber: string;
  MaxAmount: number;
  MandateType: string;
  RegistrationDate: string;
  ExpiryDate: string;
}

export type NachRegistrationStatus = 
  | 'NOT_REGISTERED'
  | 'REGISTRATION_INITIATED'
  | 'REGISTRATION_PENDING'
  | 'REGISTERED'
  | 'REGISTRATION_FAILED'
  | 'CANCELLED';

/**
 * Check if Quick Pay should be shown based on NACH status
 * Logic: Show Quick Pay if NACH is not in pending state (within 4 days of initiation)
 */
export function shouldShowQuickPay(
  nachRepayment: NachRepayment | null,
  nachRepaymentArray: NachRepayment[],
  nachRepaymentError: boolean,
  amountToPay: number,
  loanStatus: string
): 0 | 1 | 2 | 3 {
  const oneDay = 24 * 60 * 60 * 1000;
  const today = new Date();
  today.setHours(0, 0, 0, 0);

  // Hidden state conditions
  if (amountToPay < 1 || !['InArrears', 'Active', 'Late', 'Paused'].includes(loanStatus)) {
    return 0; // Hidden State
  }

  // If NACH error or no NACH data
  if (!nachRepayment || nachRepaymentError) {
    return 1; // Active State
  }

  const nachDate = new Date(nachRepayment.StartedOn);
  nachDate.setHours(23, 59, 59);
  let diffDays = Math.floor(Math.abs((nachDate.getTime() - today.getTime()) / oneDay));
  
  if (diffDays > 7) {
    diffDays = Infinity;
  }

  // Check if NACH error exists for InArrears loans
  if (loanStatus === 'InArrears' && nachRepaymentError) {
    return 1; // Active State
  }

  // Check NACH array for recent pending transactions
  if (nachRepaymentArray && nachRepaymentArray.length > 0) {
    let pendingCount = 0;
    
    for (const element of nachRepaymentArray) {
      const elementDate = new Date(element.StartedOn);
      elementDate.setHours(23, 59, 59);
      const elementDiffDays = Math.floor(Math.abs((elementDate.getTime() - today.getTime()) / oneDay));
      
      if (elementDiffDays < 4) {
        // If IsSuccess is null/undefined, it's still pending
        if (typeof element.IsSuccess !== 'boolean') {
          pendingCount++;
        }
      }
    }
    
    return pendingCount > 0 ? 2 : 1;
  }

  // Default: if more than 4 days since NACH initiation
  if (!isNaN(diffDays) && diffDays >= 4) {
    return 1; // Active State
  }

  return 2; // Inactive State (NACH pending)
}

/**
 * Create a default NachRepayment object
 */
export function createDefaultNachRepayment(): NachRepayment {
  return {
    FailureReason: 0,
    LoanId: '',
    Amount: 0,
    StartedOn: '',
    CompletedOn: '',
    IsSuccess: null
  };
}
